// Feature #313 — the capability catalogue in one person's context.
//
// Feature #356 — a capability badge is assigned by a Skills Manager, not
// earned from ratings. This page now answers "which do I hold?", not "where
// am I short?": each capability reads Held or Not held, a held one says who
// assigned it and when, and there is no shortfall list, no requirement
// digest and no pips — the requirement sets are retained server-side but
// inert, and nothing here reads them.
//
// Visibility: any authenticated user may look at themselves. Naming someone
// else requires sm_manager and is enforced at the API — the picker below is
// convenience, not the control. sm_admin is deliberately not enough: it
// defines the catalogue but has no standing on any individual. When a
// manager has picked someone else, the badges block above the list offers
// the same assign/remove controls as Staff Detail — literally the same
// component, so the two surfaces cannot drift.
import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/auth/useAuth'
import { UserRole } from '@/types'
import { PageHeader } from '@/components/ui/PageHeader'
import { formatDate } from '@/utils/dateFormat'
import {
  getCapabilities,
  getStaffRoster,
  SmCapabilityList,
  SmStaffRosterEntry,
} from '@/api/sm'
import { blockStyle, toggleButton, toggleGroup } from './components/SmShared'
import { CapabilityBadges, badgeTitle } from './components/CapabilityBadges'

const heldChip = (held: boolean): React.CSSProperties => ({
  display: 'inline-block',
  padding: '3px 12px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  backgroundColor: held ? '#f3e8f1' : 'var(--color-light-grey)',
  color: held ? 'var(--color-dark-magenta)' : 'var(--color-dark-grey)',
  border: held ? '1px solid var(--color-magenta-tint)' : '1px solid transparent',
})

const Capabilities: React.FC = () => {
  const { user } = useAuth()
  const roles = user?.roles || []
  const isManager =
    roles.includes(UserRole.SmManager) || roles.includes(UserRole.PlatformAdmin)

  const [data, setData] = useState<SmCapabilityList | null>(null)
  const [heldOnly, setHeldOnly] = useState(false)
  const [subjectId, setSubjectId] = useState<string>('')
  const [staff, setStaff] = useState<SmStaffRosterEntry[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    setError(null)
    getCapabilities({ userId: subjectId || undefined })
      .then(setData)
      .catch((e) =>
        setError(e?.response?.data?.detail || e?.message || 'Failed to load capabilities')
      )
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [subjectId])

  // Only managers can switch context, so only they need the roster — and
  // /sm/staff is manager-gated anyway.
  useEffect(() => {
    if (!isManager) return
    getStaffRoster()
      .then(setStaff)
      .catch(() => setStaff([]))
  }, [isManager])

  const items = useMemo(() => {
    const all = data?.items || []
    return heldOnly ? all.filter((c) => c.met) : all
  }, [data, heldOnly])

  const heldCount = (data?.items || []).filter((c) => c.met).length
  const viewingSelf = !subjectId || subjectId === user?.id
  const canManage = isManager && !viewingSelf

  return (
    <div>
      <PageHeader
        title={
          data && !viewingSelf ? `Capabilities — ${data.subject_display_name}` : 'Capabilities'
        }
      />
      <div style={{ padding: '24px', display: 'grid', gap: '24px' }}>
        {error && <div style={{ color: '#b91c1c' }}>{error}</div>}

        {/* Feature #356 — the manager's working surface for the person
            they have picked. Same component as Staff Detail. */}
        {canManage && data && (
          <CapabilityBadges
            capabilities={data.items}
            subjectLabel={data.subject_display_name}
            subjectUserId={data.subject_user_id}
            canManage
            onChanged={load}
          />
        )}

        <section style={blockStyle}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
              marginBottom: '4px',
            }}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
              {viewingSelf ? 'What you can deliver' : 'What they can deliver'}
            </h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {isManager && (
                <select
                  value={subjectId}
                  onChange={(e) => setSubjectId(e.target.value)}
                  style={{ fontSize: '13px', minHeight: '34px', padding: '4px 10px' }}
                  aria-label="View capabilities as"
                >
                  <option value="">Me</option>
                  {staff
                    .filter((s) => s.user_id !== user?.id)
                    .map((s) => (
                      <option key={s.user_id} value={s.user_id}>
                        {s.display_name}
                      </option>
                    ))}
                </select>
              )}
              <div style={toggleGroup} role="group" aria-label="Filter capabilities">
                <button
                  type="button"
                  aria-pressed={!heldOnly}
                  onClick={() => setHeldOnly(false)}
                  style={toggleButton(!heldOnly)}
                >
                  All
                </button>
                <button
                  type="button"
                  aria-pressed={heldOnly}
                  onClick={() => setHeldOnly(true)}
                  style={toggleButton(heldOnly)}
                >
                  {viewingSelf ? 'Only what I hold' : 'Only what they hold'}
                </button>
              </div>
            </div>
          </div>

          <p style={{ color: 'var(--color-dark-grey)', fontSize: '13px', margin: '4px 0 18px' }}>
            A capability badge is assigned by a Skills Manager — their statement that{' '}
            {viewingSelf ? 'you' : 'this person'} can deliver it. {heldCount} of{' '}
            {(data?.items || []).length} held.
          </p>

          {!data && !error && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}

          {data && items.length === 0 && (
            <p style={{ color: 'var(--color-dark-grey)', margin: 0 }}>
              {(data.items || []).length === 0
                ? 'No capabilities have been defined yet.'
                : 'No badges held yet — switch to All to see the full catalogue.'}
            </p>
          )}

          <div style={{ display: 'grid', gap: '10px' }}>
            {items.map((capability) => (
              <div
                key={capability.capability_id}
                title={badgeTitle(capability)}
                style={{
                  border: '1px solid var(--color-light-grey)',
                  borderLeft: `4px solid ${
                    capability.met ? 'var(--color-magenta)' : 'var(--color-light-grey)'
                  }`,
                  borderRadius: '8px',
                  padding: '14px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>{capability.name}</div>
                  {capability.description && (
                    <div
                      style={{
                        color: 'var(--color-dark-grey)',
                        fontSize: '13px',
                        marginTop: '2px',
                      }}
                    >
                      {capability.description}
                    </div>
                  )}
                  {capability.met && capability.note && (
                    <div
                      style={{
                        color: 'var(--color-dark-grey)',
                        fontSize: '13px',
                        marginTop: '6px',
                        fontStyle: 'italic',
                      }}
                    >
                      “{capability.note}”
                    </div>
                  )}
                </div>
                {/* flex-start, not flex-end: long descriptions wrap this
                    column under the text, and right-aligning pushed the Held
                    pill in by the width of the "by …" line beneath it, out
                    of line with the Not held pills (#373). */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '4px',
                    flexShrink: 0,
                  }}
                >
                  <span style={heldChip(capability.met)}>
                    {capability.met ? 'Held' : 'Not held'}
                  </span>
                  {capability.met && (
                    <span
                      style={{
                        fontSize: '12px',
                        color: 'var(--color-dark-grey)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      by {capability.assigned_by_label || 'a Skills Manager'}
                      {capability.assigned_at && ` · ${formatDate(capability.assigned_at)}`}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

export default Capabilities
